import Autocomplete from 'components/ReusableComponents/Autocomplete'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import Toggle from 'components/ReusableComponents/Toggle'
import { userPositionNames } from 'features/ManageUserDialog/userTitleTypes'
import { useInternalAdmin } from 'hooks'
import usePlausible from 'hooks/usePlausible'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

import { selectCurrentHotelName } from '../../accessors'
import ConfirmationDialog from '../../components/ReusableComponents/ConfirmationDialog'
import TextInput from '../../components/ReusableComponents/ManageHotel/TextInput'
import useFirstRender from '../../hooks/useFirstRender'
import styles from './ManageUserDialog.module.scss'
import manageUserDialogSlice, {
  addNewUser,
  editUser,
  phoneNumberChange
} from './ManageUserDialogSlice'

// all time iana timezones in the US

export const UStimeZones = {
  'America/Anchorage': 'Alaska Standard Time',
  'America/Chicago': 'Central Standard Time',
  'America/Los_Angeles': 'Pacific Standard Time',
  'America/New_York': 'Eastern Standard Time',
  'America/Phoenix': 'Mountain Standard Time',
  'Pacific/Honolulu': 'Hawaii-Aleutian Standard Time'
}

// all iana timezones in Canada

export const canadaTimeZones = {
  'America/Blanc-Sablon': 'Atlantic Standard Time',
  'America/St_Johns': 'Newfoundland Standard Time'
}

const {
  actions: {
    fullNameChange,
    emailChange,
    invoiceRecipientChange,
    dialogOpen,
    gotUserToEdit,
    positionChange,
    timezoneChange
  }
} = manageUserDialogSlice

const ManageUserDialog = ({
  handleClose,
  handleAddedUser,
  userToEdit,
  showPosition = true,
  subsidiaryId,
  isAddingDuringSetup = false
}) => {
  const currentHotelName = useSelector(selectCurrentHotelName)

  const isInternalAdmin = useInternalAdmin()

  const dispatch = useDispatch()

  useFirstRender(() => {
    dispatch(dialogOpen())
  })

  useEffect(() => {
    if (userToEdit) {
      dispatch(gotUserToEdit(userToEdit))
    }
  }, [dispatch, userToEdit])

  const formData = useSelector(s => s.manageUsers)

  const {
    userId,
    phone,
    email,
    fullName,
    position,
    isPhoneValidated,
    isEmailValidated,
    isFullNameValidated,
    timeZoneName,
    existingAccountFound,
    existingUserInfo,
    userAlreadyAdded,
    isInvoiceRecipient
  } = formData

  const { addToast } = useToasts()

  const { postEvent, plausibleEvents } = usePlausible()

  useFirstRender(() => {
    if (userToEdit) {
      postEvent(plausibleEvents.ViewEditUserDialog)
    } else {
      postEvent(plausibleEvents.ViewAddNewUserDialog)
    }
  }, [postEvent, plausibleEvents, userToEdit])

  useEffect(() => {
    if (userAlreadyAdded) {
      addToast('User already added to the hotel')
    }
  }, [addToast, userAlreadyAdded])

  async function handleConfirm({ ExistingUserAccept = false }) {
    if (userToEdit) {
      postEvent(plausibleEvents.EditUser)

      const res = await dispatch(
        editUser({
          userId,
          phone,
          email,
          fullName,
          position,
          timeZoneName,
          isInvoiceRecipient
        })
      )
      if (res.error) {
        addToast(res.error.message, {
          appearance: 'error',
          autoDismiss: true
        })
      }

      addToast('User edited succesfully', {
        appearance: 'success',
        autoDismiss: true
      })

      dispatch(dialogOpen())
      handleAddedUser(res?.payload?.user)
    } else {
      postEvent(plausibleEvents.CreateNewUser)

      const res = await dispatch(
        addNewUser({
          phone,
          email,
          fullName,
          ExistingUserAccept,
          position,
          timeZoneName,
          subsidiaryId,
          isAddingDuringSetup,
          isInvoiceRecipient
        })
      )

      if (res.payload === 'foundExistingAccount') {
        return
      }

      if (res.meta.requestStatus === 'rejected') {
        return addToast(res.error.message, {
          appearance: 'error',
          autoDismiss: true
        })
      }

      addToast(`User added succesfully!`, {
        appearance: 'success',
        autoDismiss: true
      })

      dispatch(dialogOpen())
      handleAddedUser(res?.payload?.user)
    }
  }

  async function closeDialog() {
    dispatch(dialogOpen())
    handleClose()
  }

  if (existingAccountFound) {
    return (
      <ConfirmationDialog
        title={`Existing account found`}
        positiveMessage="Add this user"
        negativeMessage="Cancel"
        onReject={closeDialog}
        onClose={closeDialog}
        onConfirm={() => handleConfirm({ ExistingUserAccept: true })}
      >
        <p>
          This phone number already belongs to this Directful user: <br />
          {existingUserInfo.firstName} {existingUserInfo.lastName} (
          {existingUserInfo.email})
        </p>
        <br />
        <p>Do you want to use existing user information above or update it?</p>
      </ConfirmationDialog>
    )
  }

  return (
    <ConfirmationDialog
      title={userToEdit ? `Edit ${userToEdit.name}` : 'Add user'}
      theme="blacklist"
      positiveMessage={userToEdit ? 'Update user' : 'Add user'}
      negativeMessage="Cancel"
      onClose={handleClose}
      onConfirm={handleConfirm}
      maxWidth={450}
      isButtonDisabled={
        !(
          isFullNameValidated &&
          isEmailValidated &&
          timeZoneName &&
          (phone ? isPhoneValidated : true)
        )
      }
    >
      <div className={styles.form}>
        <form onSubmit={e => e.preventDefault()}>
          <TextInput
            label="First and last name"
            autoFocus={true}
            onChange={e => dispatch(fullNameChange(e.target.value))}
            value={fullName}
            valid={isFullNameValidated}
          />
        </form>
        <TextInput
          label="Email"
          type="email"
          name="email"
          onChange={e => dispatch(emailChange(e.target.value))}
          value={email}
          valid={isEmailValidated}
        />
        {isInternalAdmin && (
          <div className={styles.invoiceToggle}>
            <Toggle
              label="Send invoices to this email"
              labelClass={styles.invoiceToggleLabel}
              checked={isInvoiceRecipient}
              handleChange={e => dispatch(invoiceRecipientChange(e.target.checked))}
            />
          </div>
        )}
        <TextInput
          label="Phone (optional)"
          type="tel"
          name="phone"
          onChange={e => dispatch(phoneNumberChange(e.target.value))}
          value={phone}
          valid={phone ? isPhoneValidated : true}
        />
        <Dropdown
          defaultVal="Select timezone"
          options={{ ...UStimeZones, ...canadaTimeZones }}
          isSortedAlphabetically={false}
          label="Timezone"
          value={timeZoneName}
          onChange={e => dispatch(timezoneChange(e.target.value))}
        />
        {showPosition && (
          <Dropdown
            options={userPositionNames}
            label="Role"
            value={position}
            onChange={e => dispatch(positionChange(e.target.value))}
            isSortedAlphabetically={false}
          />
        )}
      </div>
    </ConfirmationDialog>
  )
}

export default ManageUserDialog
